import { GlobalDataResolve } from '@shared/resolvers/global-data.resolve';
import { NotFoundComponent } from '@shared/components/not-found/not-found.component';
import { CompanyPanelGuard } from '@core/guards/company-panel.guard';
import { RoleGuard } from '@core/guards/role.guard';
import { CustomRoutes } from '@shared/consts/routes/custom-routes.const';
import { DEFAULT_PANEL_ROLES } from '@shared/auth/enums/app-role.enum';
import { TaskType } from '@shared/enums/task-type.enum';

export const GlobalRoutes: CustomRoutes = [
  {
    path: '',
    loadChildren: () =>
      import('@modules/authorization/authorization.module').then((m) => m.AuthorizationModule),
    resolve: { globalData: GlobalDataResolve }
  },
  {
    path: 'client-panel',
    loadChildren: () => import('@modules/client-panel/client-panel.module').then((m) => m.ClientPanelModule)
  },
  {
    path: 'project-creator',
    loadChildren: () =>
      import('@modules/project-creator/project-creator.module').then((m) => m.ProjectCreatorModule)
  },
  {
    path: 'help',
    loadChildren: () => import('@modules/help/help.module').then((m) => m.HelpModule)
  },
  {
    path: 'panel',
    resolve: { globalData: GlobalDataResolve },
    canActivateChild: [CompanyPanelGuard, RoleGuard],
    children: [
      {
        path: 'settings',
        loadChildren: () => import('@modules/settings/settings.module').then((m) => m.SettingsModule),
        resolve: { globalData: GlobalDataResolve },
        canActivate: [CompanyPanelGuard]
      },
      {
        path: 'my-work',
        loadChildren: () => import('@modules/my-work/my-work.module').then((m) => m.MyWorkModule),
        data: { roles: DEFAULT_PANEL_ROLES }
      },
      {
        path: 'calendar',
        loadChildren: () => import('@modules/calendar/calendar.module').then((m) => m.CalendarModule),
        data: { roles: DEFAULT_PANEL_ROLES }
      },
      {
        path: 'gallery',
        name: 'gallery',
        data: { roles: DEFAULT_PANEL_ROLES, metaTitle: 'Gallery.title' },
        loadComponent: () =>
          import('@modules/gallery/project-site-diary-gallery.component').then(
            (x) => x.ProjectSiteDiaryGalleryComponent
          )
      },
      {
        path: 'site-diaries',
        name: 'site-diaries',
        data: { metaTitle: 'ProjectsDashboard.siteDiaryGlobal.title' },
        loadComponent: () =>
          import('@modules/project-site-diary/global-site-diary.component').then(
            (x) => x.GlobalSiteDiaryComponent
          )
      },
      {
        path: 'site-diaries/:diaryId',
        name: 'site-diary-detail',
        data: {
          metaTitle: 'Projects.siteDiaryDetail',
          siteDiaryBackRoute: 'site-diaries'
        },
        loadComponent: () =>
          import('@modules/projects/modules/project-site-diary-detail/project-site-diary-detail.component').then(
            (x) => x.ProjectSiteDiaryDetailComponent
          )
      },
      {
        path: 'projects/list',
        name: 'projects-list',
        data: { roles: DEFAULT_PANEL_ROLES },
        loadComponent: () =>
          import('@modules/projects/modules/projects-list/pages/projects-list/projects-list.component').then(
            (x) => x.ProjectsListComponent
          )
      },
      {
        path: 'projects/timeline',
        name: 'projects-timeline',
        data: { roles: DEFAULT_PANEL_ROLES },
        loadChildren: () =>
          import('@modules/projects/modules/projects-timeline/projects-timeline.module').then(
            (m) => m.ProjectsTimelineModule
          )
      },
      {
        path: 'projects/resources',
        name: 'projects-resources',
        data: { roles: DEFAULT_PANEL_ROLES },
        loadComponent: () =>
          import('@modules/projects/modules/projects-resources/projects-resources.component').then(
            (x) => x.ProjectsResourcesComponent
          )
      },
      {
        path: 'projects/map',
        name: 'projects-map',
        data: { roles: DEFAULT_PANEL_ROLES },
        loadComponent: () =>
          import('@modules/projects/modules/projects-map/projects-map.component').then(
            (x) => x.ProjectsMapComponent
          )
      },
      {
        path: 'projects/preview',
        data: { roles: DEFAULT_PANEL_ROLES },
        loadChildren: () =>
          import('@modules/projects/modules/project-preview/project-preview.module').then(
            (m) => m.ProjectPreviewModule
          )
      },

      {
        path: 'projects/dashboard/:projectId',
        data: { roles: DEFAULT_PANEL_ROLES },
        loadComponent: () =>
          import('@modules/projects/modules/project-dashboard/project-dashboard.component').then(
            (x) => x.ProjectDashboardComponent
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'summary'
          },
          {
            path: 'summary',
            name: 'project-dashboard-summary',
            data: { metaTitle: 'Projects.summary' },
            loadComponent: () =>
              import('@modules/projects/modules/project-summary-v2/project-summary.component').then(
                (x) => x.ProjectSummaryComponent
              )
          },
          {
            path: 'logbook',
            name: 'project-dashboard-logbook',
            data: { metaTitle: 'ProjectsDashboard.logbookPage.title' },
            loadComponent: () =>
              import('@modules/projects/modules/project-logbook/project-logbook.component').then(
                (x) => x.ProjectLogbookComponent
              )
          },
          {
            path: 'site-diary',
            name: 'project-dashboard-site-diary',
            data: { metaTitle: 'ProjectsDashboard.siteDiaryPage.title' },
            loadComponent: () =>
              import('@modules/projects/modules/project-site-diary/project-site-diary.component').then(
                (x) => x.ProjectSiteDiaryComponent
              )
          },
          {
            path: 'site-diary/:diaryId',
            name: 'project-dashboard-site-diary-detail',
        data: { metaTitle: 'Projects.siteDiaryDetail' },
            loadComponent: () =>
              import('@modules/projects/modules/project-site-diary-detail/project-site-diary-detail.component').then(
                (x) => x.ProjectSiteDiaryDetailComponent
              )
          },
          {
            path: 'stage-tasks/:stage',
            name: 'project-dashboard-stage-tasks',
            data: { metaTitle: 'Projects.tasks', type: TaskType.TYPE_TASK, notRestorationPosition: true },
            loadComponent: () =>
              import(
                '@modules/projects/modules/project-dashboard/pages/project-stage-tasks/project-stage-tasks.component'
              ).then((x) => x.ProjectStageTasksComponent)
          },
          {
            path: 'calendar',
            name: 'project-dashboard-calendar',
            data: {
              metaTitle: 'Projects.calendar',
              actionButtonText: 'Projects.newEvent',
              actionButtonIcon: './assets/img/ic-plus-add.svg'
            },
            canActivate: [],
            loadChildren: () =>
              import('@modules/projects/modules/project-calendar/project-calendar.module').then(
                (m) => m.ProjectCalendarModule
              )
          },
          {
            path: 'tasks',
            name: 'project-dashboard-tasks',
            data: {
              metaTitle: 'Projects.tasks',
              type: TaskType.TYPE_TASK,
              notRestorationPosition: true,
              actionButtonText: 'Projects.new.' + TaskType.TYPE_TASK,
              actionButtonIcon: './assets/img/ic-plus-add.svg'
            },
            canActivate: [],
            loadComponent: () =>
              import('@modules/projects/modules/project-tasks/pages/project-tasks/project-tasks.component').then(
                (m) => m.ProjectTasksComponent
              )
          },
          {
            path: 'orders',
            name: 'project-dashboard-orders',
            data: {
              metaTitle: 'Projects.orders',
              type: TaskType.TYPE_ORDER,
              notRestorationPosition: true,
              actionButtonText: 'Projects.new.' + TaskType.TYPE_ORDER,
              actionButtonIcon: './assets/img/ic-plus-add.svg'
            },
            canActivate: [],
            loadComponent: () =>
              import('@modules/projects/modules/project-tasks/pages/project-tasks/project-tasks.component').then(
                (m) => m.ProjectTasksComponent
              )
          },
          {
            path: 'corrections',
            name: 'project-dashboard-corrections',
            data: {
              metaTitle: 'Projects.corrections',
              type: TaskType.TYPE_CORRECTION,
              notRestorationPosition: true,
              actionButtonText: 'Projects.new.' + TaskType.TYPE_CORRECTION,
              actionButtonIcon: './assets/img/ic-plus-add.svg'
            },
            canActivate: [],
            loadComponent: () =>
              import('@modules/projects/modules/project-tasks/pages/project-tasks/project-tasks.component').then(
                (m) => m.ProjectTasksComponent
              )
          },
          {
            path: 'team',
            name: 'project-dashboard-team',
            data: {
              metaTitle: 'Projects.team',
              notRestorationPosition: false,
              actionButtonText: 'ProjectTeam.addNewTeamMember',
              actionButtonIcon: './assets/img/ic_plus_contact_white.svg'
            },
            canActivate: [],
            loadChildren: () =>
              import('@modules/projects/modules/project-team/project-team.module').then(
                (m) => m.ProjectTeamModule
              )
          },
          {
            path: 'contacts',
            name: 'project-dashboard-contacts',
            data: {
              metaTitle: 'Projects.contacts',
              notRestorationPosition: false,
              actionButtonText: 'ProjectContacts.addNewContact',
              actionButtonIcon: './assets/img/ic_plus_contact_white.svg'
            },
            canActivate: [],
            loadChildren: () =>
              import('@modules/projects/modules/project-contacts/project-contacts.module').then(
                (m) => m.ProjectContactsModule
              )
          },
          {
            path: 'configurator',
            name: 'project-dashboard-configurator',
            data: {
              metaTitle: 'Projects.projectConfigurator',
              notRestorationPosition: true
            },
            canActivate: [],
            loadChildren: () =>
              import('@modules/projects/modules/projects-configurator/projects-configurator.module').then(
                (m) => m.ProjectsConfiguratorModule
              )
          },
          {
            path: 'changes',
            name: 'project-dashboard-changes',
            data: {
              metaTitle: 'Projects.changes',
              notRestorationPosition: false,
              actionButtonText: 'ProjectChanges.newChange',
              actionButtonIcon: './assets/img/ic-plus-add.svg'
            },
            canActivate: [],
            loadChildren: () =>
              import('@modules/projects/modules/project-changes/project-changes.module').then(
                (m) => m.ProjectChangesModule
              )
          }
        ]
      },

      {
        path: 'projects/create',
        name: 'project-create',
        data: { roles: DEFAULT_PANEL_ROLES },
        loadChildren: () =>
          import('@modules/projects/modules/projects-create/projects-create.module').then(
            (m) => m.ProjectsCreateModule
          )
      },
      {
        path: 'projects/edit',
        name: 'project-edit',
        data: { roles: DEFAULT_PANEL_ROLES },
        loadChildren: () =>
          import('@modules/projects/modules/projects-edit/projects-edit.module').then(
            (m) => m.ProjectsEditModule
          )
      },
      {
        path: 'contacts',
        loadChildren: () => import('@modules/contacts/contacts.module').then((m) => m.ContactsModule),
        data: { roles: DEFAULT_PANEL_ROLES }
      },
    ]
  },
  { path: '404', name: '404', component: NotFoundComponent },
  { path: '**', redirectTo: '/404' }
];
